import React from "react";
import {Formik, Form} from "formik";
import {RouteComponentProps} from "../../routers/withRouterProps";
import {useMutation, useQuery, MutationStatus} from "react-query";
import {validator} from "@reside/forms";
import {
  styled,
  PublicFormLayout,
  Button,
  Paragraph,
  PasswordField,
} from "@reside/ui";
import {ChangePasswordDto} from "../../utils/types/user";

import {UserService} from "../../services/UserService";
import {RequestError} from "../../atoms/error";
import {ButtonLink} from "../../atoms/button-link";
import {withRouterProps} from "../../routers/withRouterProps";

type ChangePasswordRouteParams = Readonly<{
  token: string;
}>;

export const PagePasswordChange = withRouterProps(
  ({
    match: {
      params: {token},
    },
  }: RouteComponentProps<ChangePasswordRouteParams>) => {
    const {mutate, status, error} = useMutation(UserService.changePassword);
    const {isLoading: isCheckingToken, error: tokenError} = useQuery(
      ["validate-reset-token", token],
      () => UserService.validateResetToken(token),
      // Asked once: a successful reset spends the token, so a refetch would report the link dead and
      // contradict the success message on screen.
      {
        retry: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        staleTime: Infinity,
      },
    );

    return (
      <Formik<ChangePasswordDto>
        initialValues={{
          password: "",
          confirmPassword: "",
          token,
        }}
        onSubmit={values => mutate(values)}
        validate={values =>
          validator.validateAll(
            values,
            {
              password: "required|min:8",
              confirmPassword: "required|same:password",
            },
            {
              same: "New password and confirmation password do not match.",
            },
          )
        }
      >
        {({submitForm}) => (
          <PagePasswordChangeRenderer
            status={status}
            error={error}
            isCheckingToken={isCheckingToken}
            tokenError={tokenError}
            onSubmit={submitForm}
          />
        )}
      </Formik>
    );
  },
);

type Props = Readonly<{
  status: MutationStatus;
  error: any;
  isCheckingToken: boolean;
  tokenError: any;
  onSubmit: () => void;
}>;

export const PagePasswordChangeRenderer = ({
  status,
  error,
  isCheckingToken,
  tokenError,
  onSubmit,
}: Props) => (
  <PublicFormLayout
    title="Create Password"
    footer={
      <Center>
        <ButtonLink
          to="/admin"
          underline
          style={{border: "none", background: "transparent"}}
        >
          Return to Log In
        </ButtonLink>
      </Center>
    }
  >
    {status === "success" ? (
      <Center>
        <Paragraph fontWeight="medium" fontSize={3}>
          Password was changed successfully.
        </Paragraph>
      </Center>
    ) : isCheckingToken ? (
      <Center>
        <Paragraph fontWeight="medium" fontSize={3}>
          Checking your link...
        </Paragraph>
      </Center>
    ) : tokenError ? (
      <Center>
        <RequestError payload={tokenError} />
      </Center>
    ) : (
      <Form>
        <PasswordField name="password" label="Password" />
        <PasswordField name="confirmPassword" label="Confirm Password" />
        {error && <RequestError payload={error} />}
        <ButtonCenter>
          <Button
            type="submit"
            color="primary"
            disabled={status === "loading"}
            onClick={onSubmit}
          >
            {status === "loading" ? <>Submitting...</> : <>Submit</>}
          </Button>
        </ButtonCenter>
      </Form>
    )}
  </PublicFormLayout>
);

const ButtonCenter = styled.div`
  width: fit-content;
  margin: 18px auto 0;
`;

const Center = styled.div`
  margin: 0 auto;
  text-align: center;
`;
